import { useState } from "react";
import { Link } from "react-router-dom";
import { Users } from "lucide-react";
import { Badge, Button, Field, Page, Spinner } from "../../components";
import { useCreatePractice, usePractices } from "../../api/hooks";
import { t } from "../../i18n/t";
import { roleLabel } from "./roleLabel";

/**
 * `/practice`: the tenant-switcher / landing page for practice mode (P5), redrawn as PracticesLanding.html —
 * a 3-column grid of practice cards (44 px initial tile, name, member count, role pill and "Open →"), the
 * dashed new-practice form card, and the empty state with the `users` icon when the caller has zero
 * memberships — waiting on a LuciBook sync that may never come for a fresh deployment is not an option
 * (P5 §5) — and a small create affordance alongside the ordinary list.
 */
export function PracticesLandingScreen(): JSX.Element {
  const practices = usePractices();
  const createPractice = useCreatePractice();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  if (practices.isPending) {
    return (
      <Page title={t("practice.landing.title")}>
        <Spinner />
      </Page>
    );
  }

  const items = practices.data?.items ?? [];
  const showCreateForm = items.length === 0 || creating;

  async function onCreate(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    try {
      await createPractice.mutateAsync({ name });
      setName("");
      setCreating(false);
    } catch {
      setError(t("error.unexpected"));
    }
  }

  return (
    <Page
      eyebrow={t("operator.eyebrow")}
      title={t("practice.landing.title")}
      caption={t("practice.landing.caption")}
      actions={
        items.length === 0 ? undefined : (
          <Button onClick={() => setCreating((prev) => !prev)}>
            {creating ? t("practice.landing.cancel_new") : t("practice.landing.new_practice")}
          </Button>
        )
      }
    >
      <div className="grid-3">
        {items.map((row) => (
          <Link
            key={row.id}
            className="pc"
            to={`/practice/${row.id}`}
            aria-label={t("practice.landing.open", { name: row.name })}
          >
            <div className="pc__head">
              <span className="prtile prtile--44" aria-hidden="true">
                {row.name.slice(0, 1).toUpperCase() || "P"}
              </span>
              <div className="pc__text">
                <span className="pc__name">{row.name}</span>
                <span className="pc__sub">
                  {t("practice.landing.member_count", { count: row.member_count })}
                </span>
              </div>
            </div>
            <div className="pc__foot">
              <Badge tone={row.role === "OWNER" ? "accent" : "neutral"}>{roleLabel(row.role)}</Badge>
              <span className="pc__open">{t("practice.landing.open_link")}</span>
            </div>
          </Link>
        ))}

        {showCreateForm ? (
          <form
            className="pcform"
            aria-label={t("practice.landing.new_practice")}
            onSubmit={(event) => void onCreate(event)}
          >
            <span className="pcform__title">{t("practice.landing.new_practice")}</span>
            <Field label={t("practice.landing.name_label")} error={error}>
              {({ id, describedBy }) => (
                <input
                  id={id}
                  aria-describedby={describedBy}
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                />
              )}
            </Field>
            <div className="row">
              <Button
                type="submit"
                variant="primary"
                disabled={createPractice.isPending || name.trim() === ""}
              >
                {t("practice.landing.create_submit")}
              </Button>
              {items.length === 0 ? null : (
                <Button className="button--text" onClick={() => setCreating(false)}>
                  {t("practice.landing.cancel_new")}
                </Button>
              )}
            </div>
          </form>
        ) : null}
      </div>

      {items.length === 0 ? (
        <section className="landingempty" aria-labelledby="landing-empty-title">
          <span className="landingempty__icon" aria-hidden="true">
            <Users size={26} strokeWidth={2} />
          </span>
          <div className="landingempty__text">
            <span className="eyebrow">{t("practice.landing.empty_eyebrow")}</span>
            <h2 className="landingempty__title" id="landing-empty-title">
              {t("practice.landing.create_first_title")}
            </h2>
            <span className="muted">{t("practice.landing.create_first_body")}</span>
          </div>
          <Button
            variant="primary"
            onClick={() => document.querySelector<HTMLElement>(".pcform input")?.focus()}
          >
            {t("practice.landing.create_submit")}
          </Button>
        </section>
      ) : null}
    </Page>
  );
}
